import React, { FC, ReactNode } from 'react';

import { useTheme } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { TFunction } from 'i18next';
import { Link } from 'react-router-dom';

import CourseIcon from '~/assets/icons/CourseIcon';
import CardAccompanyingInfo from '~/components/Card/components/CardAccompanyingInfo/CardAccompanyingInfo';
import CardCourseActionBlock from '~/components/Card/components/CardActionBlocks/CardCourseActionBlock';
import CardPathwayActionBlock from '~/components/Card/components/CardActionBlocks/CardPathwayActionBlock';
import CardDurationInfo from '~/components/Card/components/CardDurationInfo/CardDurationInfo';
import CardTitle from '~/components/Card/components/CardHeader/CardTitle';
import CardImageLabel from '~/components/Card/components/CardImageLabel/CardImageLabel';
import CardLabel from '~/components/Card/components/CardLabel/CardLabel';
import CardPlayIcon from '~/components/Card/components/CardPlayIcon/CardPlayIcon';
import CardProgressLabel from '~/components/Card/components/CardProgressLabel/CardProgressLabel';
import CardRepeatIcon from '~/components/Card/components/CardRepeatIcon/CardRepeatIcon';
import PathwayLogo from '~/components/TestResults/components/PathwayLogo';
import ToggleFromList from '~/components/ToggleFromList/ToggleFromList';
import CategoryLabel from '~/components/UI/CategoryLabel/CategoryLabel';
import CategoryLabelWrapper from '~/components/UI/CategoryLabel/CategoryLabelWrapper';
import DifficultyIcon from '~/components/UI/DifficultyIcon/DifficultyIcon';
import ProgressLine from '~/components/UI/ProgressLine/ProgressLine';
import { useTenantContext } from '~/context/TenantProvider';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import useIsSsku from '~/hooks/tenant/useIsSsku';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import GlossaryHit from '~/pages/DiscoverPage/components/GlossaryHit/GlossaryHit';
import GeneralizedHit from '~/pages/DiscoverPage/components/Hit/GeneralizedHit';
import { AggregatorHit } from '~/pages/DiscoverPage/const';
import { HITS_TYPES, PUBLISH_STATUSES } from '~/pages/const';
import {
  buildCourseRoute,
  buildExpertRoute,
  buildGlossaryRoute,
  buildInsightsRoute,
  buildPathwayRoute,
} from '~/routes';
import { css } from '~/styled';
import { MIDDLE_MARGIN_PX, SMALL_MARGIN_PX, TINY_MARGIN_PX } from '~/theme';
import {
  Course,
  CourseStatus,
  DashVideo,
  DiscoverPathwaysDetailedInfo,
  Maybe,
  Skill,
} from '~/types';
import { WIDE_CATEGORY_LABEL_WIDTH } from '~/utils/const';
import { formatDateToNewsFormat } from '~/utils/dateUtils/formatDateToNewsFormat';
import { secondsToHHHmmm } from '~/utils/dateUtils/secondsToHHHmmm';
import { secondsToHms } from '~/utils/dateUtils/secondsToHms';
import getAttributeByLocale from '~/utils/getAttributeByLocale';
import { createCategoryLink } from '~/utils/urlUtils/createCategoryLink';
import { calcVideoProgressAndLinkToShow } from '~/utils/videoProgressUtils';

type GetCategoriesArrayOptions = {
  item: Omit<AggregatorHit, 'id'> & { id?: number };
  isGridHit?: boolean;
  locale?: string;
  fullWidth?: boolean;
  availableIDs?: string[];
};

export type RefinedSkill = Omit<Skill, 'tenants'> & { tenants: Array<number> };

const transformSkills = (
  skillsArray: RefinedSkill[],
  locale: string,
  tenantId?: string,
): RefinedSkill[] => {
  if (!tenantId) return [];

  return skillsArray
    ?.filter((skill) => skill.tenants?.includes(Number(tenantId)))
    .map((skill) => ({ ...skill, title: getAttributeByLocale(skill.title, locale) }));
};

export const getCategoriesArray = ({
  item,
  isGridHit = false,
  locale = 'en',
  fullWidth = false,
  availableIDs,
}: GetCategoriesArrayOptions) => {
  const res: ReactNode[] = [];

  const parsedAvailableIDs = Array.isArray(availableIDs)
    ? availableIDs.map((id) => parseInt(id))
    : [];

  const searchableType = item?.searchable_type?.toString();
  if (searchableType === HITS_TYPES.VIDEO || searchableType === HITS_TYPES.PATHWAY) {
    if (item?.categories_array) {
      const filteredCategoriesArr = item.categories_array
        .filter((item) => item?.id && item?.tenants?.some((id) => parsedAvailableIDs.includes(id)))
        .filter((item) => item?.id && (getAttributeByLocale(item?.title, locale) || item?.title));

      if (isGridHit) {
        const categoryLink = createCategoryLink(filteredCategoriesArr?.[0]);
        filteredCategoriesArr.length &&
          res.push(
            <CategoryLabel
              key={filteredCategoriesArr[0]?.id}
              name={
                getAttributeByLocale(filteredCategoriesArr[0]?.title, locale) ||
                filteredCategoriesArr[0]?.title
              }
              textMaxWidth={fullWidth ? '100%' : WIDE_CATEGORY_LABEL_WIDTH}
              url={categoryLink}
            />,
          );
      } else {
        filteredCategoriesArr.forEach((cat) => {
          const categoryLink = createCategoryLink(cat);
          res.push(
            <CategoryLabel
              key={cat?.id}
              //TODO: remove fallback `|| cat?.title` once BE is ready
              name={getAttributeByLocale(cat?.title, locale) || cat?.title}
              url={categoryLink}
              textMaxWidth={fullWidth ? '100%' : WIDE_CATEGORY_LABEL_WIDTH}
            />,
          );
        });
      }
    }
    if (item?.difficulty) {
      res.push(
        <DifficultyIcon
          key="difficulty"
          difficulty={{
            ...item?.difficulty,
            icon_url: item?.difficulty.icon_url || '',
            title: getAttributeByLocale(item?.difficulty?.title, locale),
          }}
        />,
      );
    }
  }

  return res;
};

export const getGeneralizedObject = ({
  hit,
  videoProgress,
  locale = 'en',
  t,
  isSsku,
  tenantId,
}: {
  locale?: string;
  videoProgress?: DashVideo | null;
  t: TFunction;
  hit: any;
  isSsku: boolean;
  tenantId?: string;
}) => {
  const searchType = hit?.searchable_type?.toString();
  switch (searchType) {
    case HITS_TYPES.VIDEO: {
      const { linkToShow, progressToShow } = calcVideoProgressAndLinkToShow(
        videoProgress?.completed,
        videoProgress?.last_position,
        hit?.duration,
        hit?.video_url,
        videoProgress?.chapter,
        videoProgress?.time,
      );

      return {
        id: hit?.id,
        type: hit?.type,
        title: getAttributeByLocale(hit?.title, locale),
        description: getAttributeByLocale(hit?.description, locale),
        preview_url: hit?.preview_url,
        user: hit?.user,
        userLink: hit?.user_slug && buildExpertRoute(hit.user_slug),
        duration: secondsToHms(hit?.duration),
        searchableType: hit.searchable_type,
        link: linkToShow,
        progress: progressToShow,
        isReactLink: true,
        publish_status: hit?.publish_status,
        skills: transformSkills(hit?.skills, locale, tenantId),
      };
    }
    case HITS_TYPES.PATHWAY: {
      return {
        id: hit?.id,
        type: hit?.type,
        title: getAttributeByLocale(hit?.title, locale),
        preview_url: hit?.image_url,
        description: getAttributeByLocale(hit?.description, locale),
        count_videos: hit?.count_videos,
        learningModulesCount: hit?.learning_modules_count,
        accompanyingText: `${t('common.learningModule', {
          count: hit?.learning_modules_count,
        })} • ${secondsToHHHmmm(hit?.duration)}`,
        searchableType: hit.searchable_type,
        categories: hit?.categories_array,
        link: buildPathwayRoute(hit.slug),
        isReactLink: true,
        slug: hit.slug,
        cpdCredits: hit?.cpd_credits,
        publish_status: hit?.publish_status,
        skills: transformSkills(hit?.skills, locale, tenantId),
      };
    }
    case HITS_TYPES.NEWS: {
      return {
        id: hit?.id,
        type: hit?.type,
        title: getAttributeByLocale(hit?.title, locale),
        preview_url: hit?.image_url,
        description: getAttributeByLocale(hit?.description, locale),
        accompanyingText:
          formatDateToNewsFormat({ dateString: hit?.published_at, locale }) + ' • ' + hit?.user,
        searchableType: hit.searchable_type,
        link: buildInsightsRoute(hit.slug || hit.id),
        isReactLink: true,
      };
    }
    case HITS_TYPES.COURSE: {
      return {
        id: hit?.id,
        type: hit?.type,
        title: getAttributeByLocale(hit?.title, locale),
        description: getAttributeByLocale(hit?.description, locale),
        preview_url: hit?.image_url,
        searchableType: hit.searchable_type,
        link: buildCourseRoute(hit.slug),
        accompanyingText:
          t(`common.pathway${isSsku ? '_ssku' : ''}`, { count: hit?.count_pathways }) +
          ' • ' +
          secondsToHHHmmm(hit?.duration),
        svg_icon: hit?.svg_icon,
        publish_status: hit?.publish_status,
      };
    }
    default:
      return null;
  }
};

interface HitProps {
  hit: any;
  pathwayProgress?: DiscoverPathwaysDetailedInfo | null;
  videoProgress?: DashVideo | null;
  isInWatchlist?: boolean;
  onClick: () => void;
  coursesByIdMap?: { [key: string]: Maybe<Course> };
}

const Hit: FC<HitProps> = ({
  hit,
  pathwayProgress,
  coursesByIdMap,
  videoProgress,
  isInWatchlist = false,
  onClick,
}) => {
  const theme = useTheme();
  const { t, i18n } = useTenantTranslation();

  const { tenant } = useTenantContext();
  const { suiteIDs } = useUserInfoContext();

  const isSsku = useIsSsku();
  const data = getGeneralizedObject({
    hit,
    t,
    videoProgress,
    locale: i18n.language,
    isSsku,
    tenantId: tenant?.id || '',
  });

  const tenantIDs = typeof tenant?.id === 'string' ? [tenant.id] : [];

  const categories = getCategoriesArray({
    item: hit,
    isGridHit: false,
    locale: i18n.language,
    availableIDs: suiteIDs.length > 0 ? suiteIDs : tenantIDs,
  });
  const searchType = hit?.searchable_type?.toString();

  const { userInfo } = useUserInfoContext();

  if (searchType === HITS_TYPES.GLOSSARY) {
    return (
      <GlossaryHit
        link={buildGlossaryRoute(hit?.slug)}
        title={getAttributeByLocale(hit?.title, i18n.language)}
        description={getAttributeByLocale(hit?.description, i18n.language)}
        categories={hit?.categories_array}
        videoUrl={hit?.video_url}
        onClick={onClick}
      />
    );
  }

  const comingSoon = data?.publish_status === PUBLISH_STATUSES.ComingSoon;
  const hasPathwayProgress =
    searchType === HITS_TYPES.PATHWAY &&
    !!pathwayProgress?.count_completed_learning_modules &&
    !!data?.learningModulesCount;
  const hasCourseProgress = searchType === HITS_TYPES.COURSE && coursesByIdMap;
  const hash =
    pathwayProgress?.passed_quizes && pathwayProgress.passed_quizes[0]
      ? pathwayProgress.passed_quizes[0]?.hash
      : undefined;
  const passDate =
    pathwayProgress?.passed_quizes && pathwayProgress.passed_quizes[0]
      ? pathwayProgress.passed_quizes[0]?.date_passing
      : undefined;
  const testId =
    pathwayProgress?.not_passed_quizes?.[0]?.id ||
    pathwayProgress?.passed_quizes?.[0]?.id_passed_quiz;
  const hasUncompletedQuizes = Boolean(pathwayProgress?.not_passed_quizes?.length);

  let previewLayout: ReactNode;
  let cpdCreditsLayout: ReactNode;
  let titleLayout: ReactNode;

  switch (searchType) {
    case HITS_TYPES.VIDEO:
      previewLayout = (
        <>
          {!comingSoon ? (
            videoProgress?.completed ? (
              <CardRepeatIcon data-testid="repeatIcon" />
            ) : (
              <CardPlayIcon data-testid="playIcon" />
            )
          ) : (
            <></>
          )}
          {videoProgress?.completed && (
            <CardLabel data-testid="completedLabel">{t('page.pathway.completed')}</CardLabel>
          )}
          {data?.progress && <ProgressLine width={data.progress} />}
        </>
      );
      break;
    case HITS_TYPES.PATHWAY:
      previewLayout = (
        <>
          <CardImageLabel
            name={t('page.discover.pathway')}
            bgColor={theme.palette.common.pink}
            textColor={theme.palette.common.button.color}
          />
          {!!(pathwayProgress?.count_completed_learning_modules && data?.learningModulesCount) && (
            <>
              {hash || !testId ? (
                <CardLabel data-testid="pathwayCardLabel">
                  {hasUncompletedQuizes ? t('page.pathway.watched') : t('page.pathway.completed')}
                </CardLabel>
              ) : (
                <CardProgressLabel
                  data-testid="pathwayCardLabel"
                  current={pathwayProgress.count_completed_learning_modules}
                  total={data.learningModulesCount}
                />
              )}
              <ProgressLine
                width={
                  (pathwayProgress.count_completed_learning_modules * 100) /
                  data.learningModulesCount
                }
              />
            </>
          )}
        </>
      );

      cpdCreditsLayout = !isSsku && !!hit?.cpd_credits && (
        <StyledCategoryLabelWrapper bgColor={theme.palette.common.violetAnalyticsCard}>
          <CpdScore>{hit?.cpd_credits}</CpdScore>
          <CpdTitle>{t('common.cpdCredits')}</CpdTitle>
        </StyledCategoryLabelWrapper>
      );
      break;
    case HITS_TYPES.NEWS:
      previewLayout = (
        <CardImageLabel
          name={t('page.discover.insight')}
          bgColor={theme.palette.common.blueLight}
          textColor={theme.palette.common.white}
        />
      );
      break;
    case HITS_TYPES.COURSE:
      titleLayout = (
        <TitleWrapper data-testid="resultItemTitle">
          <StyledPathwayLogo
            circleUrl={tenant?.circle_image_url}
            icon={
              data?.svg_icon || (
                <CourseIcon secondaryColor={alpha(theme.palette.common.white, 0.33)} />
              )
            }
          />
          <StyledLink to={data?.link || ''} comingSoon={comingSoon}>
            <CourseTitle text={data?.title} />
          </StyledLink>
        </TitleWrapper>
      );
  }

  const accompanyingInfoLayout = (
    <>
      {data?.accompanyingText ? (
        <CardAccompanyingInfo text={data.accompanyingText} />
      ) : data?.user || data?.duration ? (
        <CardDurationInfo
          durationName={data?.user}
          duration={data?.duration}
          link={data?.userLink}
          reverse={data?.searchableType === HITS_TYPES.NEWS}
        />
      ) : null}
      {searchType === HITS_TYPES.VIDEO && userInfo && (
        <ToggleFromList existInWatchlist={isInWatchlist} id={data?.id} />
      )}
    </>
  );

  const courseData = coursesByIdMap?.[data?.id];

  return (
    <>
      <StyledGeneralizedHit
        publish_status={data?.publish_status}
        onClick={onClick}
        link={data?.link}
        previewImg={data?.preview_url}
        categories={categories}
        title={data?.title}
        description={data?.description}
        titleLayout={titleLayout}
        accompanyingInfoLayout={accompanyingInfoLayout}
        previewLayout={previewLayout}
        cpdCreditsLayout={cpdCreditsLayout}
        cardImgWrapperCss={videoProgress?.completed ? cardImgCompletedWrapperCss : undefined}
        noMb={hasPathwayProgress}
        skills={data?.skills}
      />
      {hasPathwayProgress && (
        <CardPathwayActionBlock
          passedQuizes={pathwayProgress?.passed_quizes}
          pathwayId={data?.id}
          category={data?.categories?.[0]?.title}
          wrapperCss={cardPathwayActionBlockWrapperCss}
          pathwaySlug={data?.slug}
          totalModules={data.learningModulesCount}
          completedModules={pathwayProgress?.count_completed_learning_modules ?? 0}
          hash={hash}
          testId={testId}
          passDate={passDate}
          pathwayName={data?.title}
          cpdCredits={hit?.cpd_credits}
        />
      )}
      {hasCourseProgress &&
        (courseData?.status === CourseStatus.Started ||
          courseData?.status === CourseStatus.Completed) && (
          <CardCourseActionBlock data={courseData} wrapperCss={cardPathwayActionBlockWrapperCss} />
        )}
    </>
  );
};

export default Hit;

const StyledLink = styled(Link, { shouldForwardProp: (prop) => prop !== 'comingSoon' })<{
  comingSoon: boolean;
}>`
  pointer-events: ${({ comingSoon }) => (comingSoon ? 'none' : 'auto')};
`;
const cardImgCompletedWrapperCss = ({ theme }) => css`
  height: 100%;
  margin-bottom: 0;

  &::after {
    content: '';
    display: block;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background: ${alpha(theme.palette.common.blueDark, 0.66)};
  }
`;
const cardPathwayActionBlockWrapperCss = ({ theme }) => css`
  margin-bottom: ${MIDDLE_MARGIN_PX};

  ${theme.breakpoints.up('md')} {
    margin-bottom: 0;
  }
`;
const StyledCategoryLabelWrapper = styled(CategoryLabelWrapper)`
  display: flex;
  align-items: center;
  margin-bottom: ${SMALL_MARGIN_PX};

  &:hover {
    box-shadow: none;
  }
`;
const CpdScore = styled('div')`
  font-weight: bold;
  color: ${({ theme }) => theme.palette.common.white};
  font-size: 20px;
  line-height: 1;

  ${({ theme }) => theme.breakpoints.up('xl')} {
    margin-right: 9px;
  }
`;
const CpdTitle = styled('div')`
  display: none;
  color: ${({ theme }) => theme.palette.common.white};
  font-size: 14px;
  line-height: 1;
  white-space: nowrap;

  ${({ theme }) => theme.breakpoints.up('xl')} {
    display: block;
  }
`;
const StyledGeneralizedHit = styled(GeneralizedHit, {
  shouldForwardProp: (prop) => prop !== 'noMb',
})<{ noMb: boolean }>`
  margin-bottom: ${({ noMb }) => noMb && 0};
`;
const TitleWrapper = styled('div')`
  display: flex;
  align-items: center;
  margin-bottom: ${TINY_MARGIN_PX};
`;
const StyledPathwayLogo = styled(PathwayLogo)`
  && {
    display: none;
    min-width: 40px;
    width: 40px;
    height: 40px;
    margin-right: ${TINY_MARGIN_PX};
    margin-bottom: 0;

    ${({ theme }) => theme.breakpoints.up('md')} {
      display: block;
    }
  }
`;
const CourseTitle = styled(CardTitle)`
  && {
    margin-bottom: 2px;
    color: ${({ theme }) => theme.palette.common.white};
  }
`;
